import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  confirmProposal,
  getProposalRevisionPublicPreview,
  getPublicProposalByShareToken,
  getPublicProposal,
  updateProposalSelection,
} from "@/lib/proposalsApi";
import { getGeneralSettings } from "@/lib/settingsApi";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import {
  Loader2,
  Calendar,
  Users,
  MapPin,
  Hotel,
  Plane,
  Ticket,
  Car,
  Clock,
  Star,
  Shield,
  CheckCircle2,
  Globe,
  Mail,
  Phone,
  MessageSquare,
  Headphones,
  ChevronDown,
  FileCheck,
  User,
  Luggage,
  PlusCircle,
  ArrowRight,
  Compass,
  Map as MapIcon,
  CreditCard,
  Banknote,
  BedDouble,
  Circle,
} from "lucide-react";
import { ProposalConfirmation } from "@/components/proposals/ProposalConfirmation";
import { ProposalCountdown } from "@/components/proposals/ProposalCountdown";
import hcteLogo from "@/assets/hcte-logo.svg";
import { getTranslation, ProposalLanguage } from "@/lib/proposalTranslations";
import {
  applyProposalPricingAdjustment,
  computeProposalFinancialSummary,
  convertUsdToCurrency,
  formatMoney,
  resolveProposalPricingView,
} from "@/lib/financialCalculations";
import { useTheme } from "next-themes";
import { getTourPriceOverride, getTourScheduledDurationHours } from "@/lib/tourDurationPricing";
import { getStatusIndex, mapLegacyStatusToNewStatus } from "@/lib/proposalStatus";
import { extractDatePart, extractTimePart, formatCalendarDate } from "@/lib/datetimeLocal";

interface GroupedService {
  type: "tour" | "attraction" | "airport_service" | "transfer" | "additional_request";
  time?: string | null;
  data: any;
}

interface DailyService {
  date: string;
  services: GroupedService[];
}

interface PublicProposalViewProps {
  proposalId?: string;
  revisionId?: string;
}

type ProposalStatusHistoryEntry = {
  status?: string | null;
  entered_at?: string | null;
};

const parseProposalTimestamp = (value?: string | null) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const getProposalAcceptedAt = (
  proposal?: {
    confirmed_at?: string | null;
    status_history?: ProposalStatusHistoryEntry[] | null;
  } | null,
) => {
  const confirmedAt = parseProposalTimestamp(proposal?.confirmed_at);
  if (confirmedAt !== null) return confirmedAt;
  if (!Array.isArray(proposal?.status_history)) return null;

  const approvedEntry = proposal.status_history
    .map((entry) => ({
      normalizedStatus: mapLegacyStatusToNewStatus(entry?.status),
      enteredAt: parseProposalTimestamp(entry?.entered_at),
    }))
    .find((entry) => entry.normalizedStatus === "approved_to_be_paid" && entry.enteredAt !== null);

  return approvedEntry?.enteredAt ?? null;
};

// Applies to proposals that were still pending client acceptance on or after March 12, 2026.
const PUBLIC_PROPOSAL_TERMS_CONFIRMATION_START = Date.parse("2026-03-12T00:00:00Z");

const ExpandableDescriptionBase = ({
  text,
  className,
  showMoreLabel,
  showLessLabel,
}: {
  text: string;
  className?: string;
  showMoreLabel: string;
  showLessLabel: string;
}) => {
  const [expanded, setExpanded] = useState(false);
  const cleanedText = text.trim();
  const shouldShowToggle = cleanedText.length > 160 || cleanedText.includes("\n");

  if (!cleanedText) return null;

  return (
    <div className="space-y-1.5">
      <p
        className={`text-sm text-muted-foreground leading-relaxed whitespace-pre-line ${
          expanded ? "" : "line-clamp-2"
        } ${className ?? ""}`}
      >
        {cleanedText}
      </p>
      {shouldShowToggle && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((prev) => !prev);
          }}
          className="inline-flex items-center text-xs font-medium text-primary hover:underline"
        >
          {expanded ? showLessLabel : showMoreLabel}
        </button>
      )}
    </div>
  );
};

const PublicProposalView = ({ proposalId: proposalIdProp, revisionId: revisionIdProp }: PublicProposalViewProps) => {
  const params = useParams();
  const id = proposalIdProp ?? params.id;
  const shareToken = params.token;
  const revisionId = revisionIdProp ?? params.revisionId;
  const isHistoryPreview = !!revisionId;
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [publicPageState, setPublicPageState] = useState<"ready" | "canceled" | "not_found">("ready");
  const [publicPageLanguage, setPublicPageLanguage] = useState<ProposalLanguage | null>(null);
  const [proposal, setProposal] = useState<any>(null);
  const [accommodations, setAccommodations] = useState<any[]>([]);
  const [flights, setFlights] = useState<any[]>([]);
  const [tours, setTours] = useState<any[]>([]);
  const [attractions, setAttractions] = useState<any[]>([]);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [airportServices, setAirportServices] = useState<any[]>([]);
  const [additionalRequests, setAdditionalRequests] = useState<any[]>([]);
  const [insurance, setInsurance] = useState<any[]>([]);
  const [handlingFee, setHandlingFee] = useState<any>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [accommodationsOpen, setAccommodationsOpen] = useState(true);
  const [flightsOpen, setFlightsOpen] = useState(true);
  const [insuranceOpen, setInsuranceOpen] = useState(true);
  const [expirationDays, setExpirationDays] = useState(30);
  const [generalTerms, setGeneralTerms] = useState<string | null>(null);
  const [paymentTerms, setPaymentTerms] = useState<string | null>(null);
  const [generalOpen, setGeneralOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [cancellationPolicyOpen, setCancellationPolicyOpen] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  const [serviceSelections, setServiceSelections] = useState<Record<string, boolean>>({});
  const [selectionsInitialized, setSelectionsInitialized] = useState(false);
  const activeProposalId = id ?? proposal?.id;

  const resolveProposalLanguage = (value?: string | null): ProposalLanguage | null => {
    return value === "pt_br" || value === "en" ? value : null;
  };

  const browserLanguage = useMemo<ProposalLanguage>(() => {
    if (typeof navigator !== "undefined" && navigator.language.toLowerCase().startsWith("pt")) {
      return "pt_br";
    }
    return "en";
  }, []);
  const effectiveLanguage = useMemo<ProposalLanguage>(() => {
    const proposalLanguage = resolveProposalLanguage(proposal?.language);
    if (proposalLanguage) return proposalLanguage;
    if (publicPageLanguage) return publicPageLanguage;
    return browserLanguage;
  }, [browserLanguage, proposal?.language, publicPageLanguage]);
  const t = useMemo(() => getTranslation(effectiveLanguage), [effectiveLanguage]);
  const locale = useMemo(() => effectiveLanguage === "pt_br" ? "pt-BR" : "en-US", [effectiveLanguage]);
  const quantityLabel = useMemo(() => (effectiveLanguage === "pt_br" ? "Qtd." : "Qty"), [effectiveLanguage]);
  const ticketLabel = useMemo(() => (effectiveLanguage === "pt_br" ? "Ingresso" : "Ticket"), [effectiveLanguage]);
  const totalInUsdLabel = useMemo(() => (effectiveLanguage === "pt_br" ? "Total em USD" : "Total in USD"), [effectiveLanguage]);
  const totalInAedLabel = useMemo(() => (effectiveLanguage === "pt_br" ? "Total em AED" : "Total in AED"), [effectiveLanguage]);
  const ticketTypeLabel = useMemo(
    () => (effectiveLanguage === "pt_br" ? "Tipos de ingresso" : "Ticket Types"),
    [effectiveLanguage]
  );

  useEffect(() => {
    if (previousThemeRef.current === null) {
      previousThemeRef.current = theme ?? "light";
    }
    if (theme !== "light") {
      setTheme("light");
    }
  }, [setTheme, theme]);

  useEffect(() => {
    return () => {
      const previousTheme = previousThemeRef.current;
      if (previousTheme && previousTheme !== "light") {
        setTheme(previousTheme);
      }
    };
  }, [setTheme]);

  const expiryDateIso = useMemo(() => {
    try {
      if (proposal?.expiry_date) return new Date(proposal.expiry_date).toISOString();
      if (proposal?.sent_at && expirationDays) {
        const d = new Date(proposal.sent_at);
        d.setDate(d.getDate() + Number(expirationDays || 0));
        return d.toISOString();
      }
    } catch {
      // Ignore invalid date parsing and keep expiry undefined.
    }
    return undefined;
  }, [proposal?.expiry_date, proposal?.sent_at, expirationDays]);

  const isExpiredNow = useMemo(() => {
    if (!expiryDateIso) return false;
    return new Date(expiryDateIso).getTime() <= Date.now();
  }, [expiryDateIso]);
  const normalizedProposalStatus = useMemo(
    () => mapLegacyStatusToNewStatus(proposal?.status),
    [proposal?.status]
  );
  const canEditProposalSelections = useMemo(
    () =>
      !isHistoryPreview &&
      (
        normalizedProposalStatus === "needs_fix_sales" ||
        (normalizedProposalStatus === "sent" && !isExpiredNow)
      ),
    [normalizedProposalStatus, isExpiredNow, isHistoryPreview]
  );
  const isProposalApproved = useMemo(() => {
    const approvedIndex = getStatusIndex("approved_to_be_paid");
    const currentIndex = getStatusIndex(normalizedProposalStatus);
    return currentIndex >= approvedIndex && normalizedProposalStatus !== "needs_fix_sales";
  }, [normalizedProposalStatus]);
  const approvedBannerMessage = useMemo(
    () =>
      effectiveLanguage === "pt_br"
        ? "Proposta aprovada."
        : "Proposal approved.",
    [effectiveLanguage]
  );
  const requiresTermsAcceptance = useMemo(() => {
    if (normalizedProposalStatus === "sent" || normalizedProposalStatus === "needs_fix_sales") {
      return true;
    }

    const acceptedAt = getProposalAcceptedAt(proposal);
    if (acceptedAt === null) {
      return false;
    }

    return acceptedAt >= PUBLIC_PROPOSAL_TERMS_CONFIRMATION_START;
  }, [normalizedProposalStatus, proposal]);

  const ExpandableDescription = useMemo(() => {
    const Component = ({ text, className }: { text: string; className?: string }) => (
      <ExpandableDescriptionBase
        text={text}
        className={className}
        showMoreLabel={t.showMore}
        showLessLabel={t.showLess}
      />
    );
    Component.displayName = "ExpandableDescription";
    return Component;
  }, [t.showMore, t.showLess]);

  const ServiceDescription = useMemo(() => {
    const Component = ({ text }: { text?: string | null }) => {
      if (!text) return null;
      return (
        <ExpandableDescriptionBase
          text={text}
          showMoreLabel={t.showMore}
          showLessLabel={t.showLess}
        />
      );
    };
    Component.displayName = "ServiceDescription";
    return Component;
  }, [t.showMore, t.showLess]);

  useEffect(() => {
    fetchProposalData();
    fetchExpirationSettings();
  }, [id, revisionId, shareToken]);

  const fetchExpirationSettings = async () => {
    try {
      const data = await getGeneralSettings();
      if (data?.expiration_days) setExpirationDays(data.expiration_days);
      if (typeof data?.general_terms === "string") setGeneralTerms(data.general_terms);
      if (typeof data?.payment_terms === "string") setPaymentTerms(data.payment_terms);
    } catch (error) {
      console.error("Error fetching expiration settings:", error);
    }
  };

  const handleAcceptProposal = async () => {
    if (!activeProposalId || isHistoryPreview) return;

    setIsAccepting(true);
    try {
      const updated = await confirmProposal(activeProposalId);
      setProposal(updated);

      toast({
        title: t.proposalConfirmed,
        description: t.thankYouMessage,
      });
    } catch (error) {
      console.error("Error accepting proposal:", error);
      const message = (error as any)?.message || (typeof error === "string" ? error : "Unknown error");
      toast({
        title: "Error",
        description: `Failed to accept proposal: ${message}`,
        variant: "destructive",
      });
    } finally {
      setIsAccepting(false);
    }
  };

  const fetchProposalData = async () => {
    setLoading(true);
    setPublicPageState("ready");
    setPublicPageLanguage(null);
    setProposal(null);
    setAccommodations([]);
    setFlights([]);
    setTours([]);
    setAttractions([]);
    setTransfers([]);
    setAirportServices([]);
    setAdditionalRequests([]);
    setInsurance([]);
    setHandlingFee(null);
    setServiceSelections({});
    setSelectionsInitialized(false);

    try {
      if (!id && !shareToken) {
        setPublicPageState("not_found");
        return;
      }

      const proposalData = shareToken
        ? await getPublicProposalByShareToken(shareToken)
        : isHistoryPreview && revisionId && id
        ? await getProposalRevisionPublicPreview(id, revisionId)
        : await getPublicProposal(id as string);
      setProposal(proposalData);

      const accommodationsData = (proposalData.accommodations || []).slice().sort((a: any, b: any) => {
        if (!a.check_in) return -1;
        if (!b.check_in) return 1;
        return a.check_in.localeCompare(b.check_in);
      });
      setAccommodations(accommodationsData);

      const flightsData = (proposalData.flights || []).slice().sort((a: any, b: any) => {
        return (a.departure_datetime || "").localeCompare(b.departure_datetime || "");
      });
      setFlights(flightsData);

      const toursData = (proposalData.tours || []).slice().sort((a: any, b: any) => {
        if (!a.tour_date) return -1;
        if (!b.tour_date) return 1;
        return a.tour_date.localeCompare(b.tour_date);
      });
      setTours(toursData);

      const attractionsData = (proposalData.attractions || []).slice().sort((a: any, b: any) => {
        if (!a.attraction_date) return -1;
        if (!b.attraction_date) return 1;
        return a.attraction_date.localeCompare(b.attraction_date);
      });
      setAttractions(attractionsData);

      const transfersData = (proposalData.transfers || []).slice().sort((a: any, b: any) => {
        if (!a.transfer_date) return -1;
        if (!b.transfer_date) return 1;
        return a.transfer_date.localeCompare(b.transfer_date);
      });
      setTransfers(transfersData);

      const airportServicesData = (proposalData.airport_services || []).slice().sort((a: any, b: any) => {
        if (!a.service_date) return -1;
        if (!b.service_date) return 1;
        return a.service_date.localeCompare(b.service_date);
      });
      setAirportServices(airportServicesData);

      const handlingFeeData = Array.isArray(proposalData.handling_fee) ? proposalData.handling_fee[0] : null;
      setHandlingFee(handlingFeeData || null);

      setAdditionalRequests(proposalData.additional_requests || []);
      setInsurance(proposalData.insurance || []);
    } catch (error: any) {
      if (!isHistoryPreview && (error?.code === "PROPOSAL_CANCELED" || error?.status === 410)) {
        setPublicPageLanguage(resolveProposalLanguage(error?.errors?.language));
        setPublicPageState("canceled");
        return;
      }

      setPublicPageState("not_found");

      if (error?.status !== 404) {
        toast({
          title: "Error loading proposal",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getAirportServicesForDate = (flightDate: string | null | undefined) => {
    if (!flightDate) return [];
    const date = extractDatePart(flightDate) ?? flightDate.split("T")[0];
    return airportServices.filter((service) => service.service_date === date);
  };

  const getStandaloneAirportServices = () => {
    const flightDates = new Set(
      flights
        .map((flight) => extractDatePart(flight.departure_datetime))
        .filter((value): value is string => Boolean(value))
    );
    return airportServices.filter((service) => !flightDates.has(service.service_date));
  };

  const getPricingTypeLabel = (pricingType: string) => {
    switch (pricingType) {
      case "per_person":
        return t.perPerson;
      case "per_family":
        return t.perFamily;
      case "per_group":
        return t.perGroup;
      default:
        return "";
    }
  };

  const getPrice = (service: any, partnerBusinessType: string, isFreeOfCharge: boolean = false) => {
    if (!service || isFreeOfCharge) return 0;

    switch (partnerBusinessType) {
      case "agency":
      case "agent":
        return service.price_ag || 0;
      case "operator":
        return service.price_op || 0;
      default:
        return service.price_cf || 0;
    }
  };

  const normalizeDateKey = (date: string | null | undefined) => {
    if (!date) return t.noDate;
    return extractDatePart(date) ?? t.noDate;
  };

  const groupServicesByDay = (): DailyService[] => {
    const servicesMap = new Map<string, DailyService>();

    const addService = (dateKey: string, service: GroupedService) => {
      if (!servicesMap.has(dateKey)) {
        servicesMap.set(dateKey, { date: dateKey, services: [] });
      }
      servicesMap.get(dateKey)!.services.push(service);
    };

    transfers.forEach((transfer) => {
      addService(normalizeDateKey(transfer.transfer_date), {
        type: "transfer",
        time: null,
        data: transfer,
      });
    });

    tours.forEach((tour) => {
      addService(normalizeDateKey(tour.tour_date), {
        type: "tour",
        time: tour.time_from,
        data: tour,
      });
    });

    attractions.forEach((attraction) => {
      addService(normalizeDateKey(attraction.attraction_date), {
        type: "attraction",
        time: attraction.time_from,
        data: attraction,
      });
    });

    additionalRequests.forEach((request) => {
      addService(normalizeDateKey(request.request_date), {
        type: "additional_request",
        time: request.request_time,
        data: request,
      });
    });

    servicesMap.forEach((day) => {
      day.services.sort((a, b) => {
        if (!a.time && !b.time) return 0;
        if (!a.time) return 1;
        if (!b.time) return -1;
        return a.time.localeCompare(b.time);
      });
    });

    return Array.from(servicesMap.values()).sort((a, b) => a.date.localeCompare(b.date));
  };

  const dailyServices = useMemo(() => groupServicesByDay(), [transfers, tours, attractions, additionalRequests]);
  const standaloneAirportServices = useMemo(() => getStandaloneAirportServices(), [airportServices, flights]);
  const getInitialSelectionValue = (value: boolean | null | undefined) => value !== false;

  useEffect(() => {
    if (loading || selectionsInitialized) return;
    
    const initialSelections: Record<string, boolean> = {};
    
    tours.forEach((t) => { initialSelections[`tour_${t.id}`] = getInitialSelectionValue(t.is_selected_by_client); });
    attractions.forEach((a) => { initialSelections[`attraction_${a.id}`] = getInitialSelectionValue(a.is_selected_by_client); });
    transfers.forEach((tr) => { initialSelections[`transfer_${tr.id}`] = getInitialSelectionValue(tr.is_selected_by_client); });
    flights.forEach((flight) => { initialSelections[`flight_${flight.id}`] = getInitialSelectionValue(flight.is_selected_by_client); });
    airportServices.forEach((as) => { initialSelections[`airport_service_${as.id}`] = getInitialSelectionValue(as.is_selected_by_client); });
    additionalRequests.forEach((ar) => { initialSelections[`additional_request_${ar.id}`] = getInitialSelectionValue(ar.is_selected_by_client); });
    insurance.forEach((ins) => { initialSelections[`insurance_${ins.id}`] = getInitialSelectionValue(ins.is_selected_by_client); });
    accommodations.forEach((acc) => { initialSelections[`accommodation_${acc.id}`] = getInitialSelectionValue(acc.is_selected_by_client); });
    
    setServiceSelections(initialSelections);
    setSelectionsInitialized(true);
  }, [loading, selectionsInitialized, tours, attractions, transfers, flights, airportServices, additionalRequests, insurance, accommodations]);

  const toggleServiceSelection = async (type: string, itemId: number | string) => {
    if (!activeProposalId || !canEditProposalSelections) return;
    const key = `${type}_${itemId}`;
    const currentValue = serviceSelections[key] ?? false;
    const newValue = !currentValue;
    const numId = typeof itemId === 'string' ? parseInt(itemId, 10) : itemId;
    
    setServiceSelections((prev) => ({ ...prev, [key]: newValue }));
    
    try {
      await updateProposalSelection(activeProposalId, { type, item_id: numId, selected: newValue });
    } catch (error: any) {
      setServiceSelections((prev) => ({ ...prev, [key]: currentValue }));
      toast({
        title: "Error saving selection",
        description: error?.message || "Failed to update selection.",
        variant: "destructive",
      });
    }
  };

  const isServiceSelected = (type: string, id: number | string): boolean => {
    const key = `${type}_${id}`;
    return serviceSelections[key] ?? true;
  };

  const isServiceMarkedByClient = (service: GroupedService): boolean => {
    const serviceId = service?.data?.id;
    if (serviceId !== null && serviceId !== undefined) {
      return isServiceSelected(service.type, serviceId);
    }
    return service?.data?.is_selected_by_client === true;
  };

  const mergedTours = useMemo(() => 
    tours.map(t => ({ ...t, is_selected_by_client: serviceSelections[`tour_${t.id}`] ?? getInitialSelectionValue(t.is_selected_by_client) })),
    [tours, serviceSelections]
  );
  
  const mergedAttractions = useMemo(() => 
    attractions.map(a => ({ ...a, is_selected_by_client: serviceSelections[`attraction_${a.id}`] ?? getInitialSelectionValue(a.is_selected_by_client) })),
    [attractions, serviceSelections]
  );
  
  const mergedTransfers = useMemo(() => 
    transfers.map(tr => ({ ...tr, is_selected_by_client: serviceSelections[`transfer_${tr.id}`] ?? getInitialSelectionValue(tr.is_selected_by_client) })),
    [transfers, serviceSelections]
  );

  const mergedFlights = useMemo(() =>
    flights.map(flight => ({ ...flight, is_selected_by_client: serviceSelections[`flight_${flight.id}`] ?? getInitialSelectionValue(flight.is_selected_by_client) })),
    [flights, serviceSelections]
  );
  
  const mergedAirportServices = useMemo(() => 
    airportServices.map(as => ({ ...as, is_selected_by_client: serviceSelections[`airport_service_${as.id}`] ?? getInitialSelectionValue(as.is_selected_by_client) })),
    [airportServices, serviceSelections]
  );
  
  const mergedAccommodations = useMemo(() => 
    accommodations.map(acc => ({ ...acc, is_selected_by_client: serviceSelections[`accommodation_${acc.id}`] ?? getInitialSelectionValue(acc.is_selected_by_client) })),
    [accommodations, serviceSelections]
  );
  
  const mergedAdditionalRequests = useMemo(() => 
    additionalRequests.map(ar => ({ ...ar, is_selected_by_client: serviceSelections[`additional_request_${ar.id}`] ?? getInitialSelectionValue(ar.is_selected_by_client) })),
    [additionalRequests, serviceSelections]
  );
  
  const mergedInsurance = useMemo(() => 
    insurance.map(ins => ({ ...ins, is_selected_by_client: serviceSelections[`insurance_${ins.id}`] ?? getInitialSelectionValue(ins.is_selected_by_client) })),
    [insurance, serviceSelections]
  );

  const proposalPartnerBusinessType = useMemo(() => {
    // A proposal-level price-tier override wins over the client's partner tier so that
    // per-item prices stay consistent with the (override-aware) grand total below.
    const override = typeof proposal?.price_tier_override === "string" ? proposal.price_tier_override.toLowerCase() : "";
    if (override && override !== "auto") {
      return override;
    }
    const raw = proposal?.clients?.partner_business_type ?? proposal?.clients?.partnerBusinessType;
    return typeof raw === "string" ? raw.toLowerCase() : "";
  }, [proposal?.clients, proposal?.price_tier_override]);

  const proposalPaxCount = useMemo(
    () => Math.max(1, (proposal?.pax_adults || 0) + (proposal?.pax_children || 0) + (proposal?.pax_infants || 0)),
    [proposal?.pax_adults, proposal?.pax_children, proposal?.pax_infants],
  );

  // Airport services are priced per unit; for per_person pricing the client owes unit × pax.
  const getAirportServicePricing = (service: any) => {
    const unit = getPrice(service?.airport_services, proposalPartnerBusinessType, service?.free_of_charge);
    const perPerson = (service?.airport_services?.pricing_type || "").toLowerCase() === "per_person";
    const quantity = perPerson ? proposalPaxCount : 1;
    return { unit, quantity, total: unit * quantity, perPerson };
  };

  const publicPricingView = useMemo(() => resolveProposalPricingView(proposal), [proposal]);
  const financialSummary = useMemo(() => computeProposalFinancialSummary({
    pax_adults: proposal?.pax_adults,
    pax_children: proposal?.pax_children,
    pax_infants: proposal?.pax_infants,
    clients: proposal?.clients || {},
    pricing_adjustment_type: proposal?.pricing_adjustment_type,
    pricing_adjustment_percentage: proposal?.pricing_adjustment_percentage,
    price_tier_override: proposal?.price_tier_override,
    public_pricing_view: proposal?.public_pricing_view,
    vat_exempt: proposal?.vat_exempt,
    proposal_tours: mergedTours,
    proposal_attractions: mergedAttractions,
    proposal_transfers: mergedTransfers,
    proposal_flights: mergedFlights,
    proposal_airport_services: mergedAirportServices,
    proposal_accommodations: mergedAccommodations,
    proposal_additional_requests: mergedAdditionalRequests,
    proposal_insurance: mergedInsurance,
    proposal_handling_fee: handlingFee ? [handlingFee] : [],
  }, { pricingView: publicPricingView }), [
    proposal?.pax_adults,
    proposal?.pax_children,
    proposal?.pax_infants,
    proposal?.clients,
    proposal?.pricing_adjustment_type,
    proposal?.pricing_adjustment_percentage,
    proposal?.price_tier_override,
    proposal?.public_pricing_view,
    proposal?.vat_exempt,
    publicPricingView,
    mergedTours,
    mergedAttractions,
    mergedTransfers,
    mergedFlights,
    mergedAirportServices,
    mergedAccommodations,
    mergedAdditionalRequests,
    mergedInsurance,
    handlingFee,
  ]);

  const currency = useMemo(
    () => ((proposal?.currency || "").toString().trim().toUpperCase() === "AED" ? "AED" : "USD"),
    [proposal?.currency]
  );
  const getAdjustedProposalPrice = useCallback(
    (value: number | string | null | undefined, options?: { exempt?: boolean }) =>
      applyProposalPricingAdjustment(value, proposal, {
        pricingView: publicPricingView,
        exempt: options?.exempt,
      }),
    [proposal, publicPricingView]
  );
  const formatProposalMoney = useCallback(
    (value: number | string | null | undefined, options?: { exempt?: boolean }) =>
      formatMoney(getAdjustedProposalPrice(value, options), currency, locale),
    [getAdjustedProposalPrice, currency, locale]
  );
  const proposalTotalPayable = financialSummary.totalPayable;
  const formatConvertedTotalValue = (value: number, targetCurrency: "USD" | "AED") =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertUsdToCurrency(value, targetCurrency));

  const totalPayableInUsd = formatConvertedTotalValue(proposalTotalPayable, "USD");
  const totalPayableInAed = formatConvertedTotalValue(proposalTotalPayable, "AED");
  const convertedTotalLabel = currency === "AED" ? totalInUsdLabel : totalInAedLabel;
  const convertedTotalValue = currency === "AED" ? totalPayableInUsd : totalPayableInAed;

const allAccommodationsFOC = useMemo(() => {
    const selected = (accommodations || []).filter((a) => a && a.is_selected_by_client !== false);
    if (selected.length === 0) return false;
    return selected.every((a) => a.free_of_charge === true);
  }, [accommodations]);

  const formatDate = (date: string | null | undefined) => {
    if (!date) return t.noDate;
    return formatCalendarDate(date, locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }) || t.noDate;
  };

  const formatWeekday = (date: string | null | undefined) => {
    if (!date) return "";
    try {
      return formatCalendarDate(date, locale, { weekday: "long" });
    } catch {
      return "";
    }
  };

  const formatDateTimeClock = (value: string | null | undefined) => {
    const timePart = extractTimePart(value);
    return timePart || "--:--";
  };

  const travelDateRange = useMemo(() => {
    return {
      start: proposal?.first_service_start_date ?? null,
      end: proposal?.last_service_end_date ?? null,
    };
  }, [proposal?.first_service_start_date, proposal?.last_service_end_date]);

  const getServiceTitle = (service: GroupedService) => {
    if (service.type === "tour") return service.data?.tours?.title || "";
    if (service.type === "attraction") {
      const attractionTitle = service.data?.attractions?.title || "";
      const selectedTicketTitle = (service.data?.attraction_tickets?.title || "").toString().trim();
      const quantity = Number(service.data?.ticket_quantity ?? 1);
      if (!selectedTicketTitle) return attractionTitle;
      return `${attractionTitle} - ${selectedTicketTitle} (${quantityLabel} ${quantity})`;
    }
    if (service.type === "transfer") return service.data?.transfers?.title || service.data?.transfer_type || t.transfer;
    if (service.type === "additional_request") {
      const requestTitle = service.data?.request_title || "";
      const ticketType = (service.data?.request_ticket_type || "").toString().trim();
      const quantity = Math.max(1, Number(service.data?.request_quantity || 1));
      if (!ticketType && quantity <= 1) return requestTitle;
      const parts = [ticketType, quantity > 1 ? `${quantityLabel} ${quantity}` : ""].filter(Boolean).join(", ");
      return parts ? `${requestTitle} (${parts})` : requestTitle;
    }
    return "";
  };

  const getTourDurationDetails = (tourData: any) => {
    const registeredRaw = tourData?.tours?.duration_hours;
    const registeredParsed = Number(registeredRaw);
    const registeredHours =
      registeredRaw === null || registeredRaw === undefined || registeredRaw === "" || !Number.isFinite(registeredParsed)
        ? null
        : registeredParsed;

    const scheduledHours = getTourScheduledDurationHours(tourData?.time_from, tourData?.time_to);
    const isExtended =
      scheduledHours !== null && registeredHours !== null && scheduledHours > registeredHours;

    return {
      registeredHours,
      scheduledHours,
      isExtended,
      displayHours: isExtended ? scheduledHours : registeredHours,
    };
  };

  const formatDurationHours = (hours: number | null | undefined) => {
    if (hours === null || hours === undefined || Number.isNaN(hours)) return "";
    return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(hours);
  };

  const getServiceDescription = (service: GroupedService) => {
    if (service.type === "tour") {
      const durationDetails = getTourDurationDetails(service.data);

      if (!durationDetails.isExtended || durationDetails.displayHours === null) {
        return service.data?.tours?.description || "";
      }

      const tourTitle = String(service.data?.tours?.title || "");
      const hasGuide = /\bguide\b/i.test(tourTitle);
      if (hasGuide) {
        return `Extended private tour with guide service for ${formatDurationHours(durationDetails.displayHours)} hours in Dubai.`;
      }
      return `Extended private tour for ${formatDurationHours(durationDetails.displayHours)} hours in Dubai.`;
    }
    if (service.type === "attraction") {
      const attractionDescription = (service.data?.attractions?.description || "").toString().trim();
      return attractionDescription;
    }
    if (service.type === "transfer") return service.data?.transfers?.description || service.data?.notes || "";
    if (service.type === "additional_request") return service.data?.request_description || "";
    return "";
  };

  const getServiceDuration = (service: GroupedService) => {
    if (service.type === "tour") return getTourDurationDetails(service.data).displayHours || null;
    if (service.type === "attraction") return service.data?.attractions?.duration_hours || null;
    return null;
  };

  const getServiceImages = (service: GroupedService): string[] => {
    const sanitizeList = (values: any[] | undefined): string[] =>
      (values || [])
        .filter((value) => typeof value === "string")
        .map((value) => String(value).trim())
        .filter((value) => value.length > 0)
        .slice(0, 3);

    if (service.type === "tour") {
      const image = service.data?.tours?.image_url;
      return image ? [image] : [];
    }
    if (service.type === "attraction") {
      const image = service.data?.attractions?.image_url;
      return image ? [image] : [];
    }
    if (service.type === "additional_request") {
      return sanitizeList(service.data?.request_images);
    }
    return [];
  };

  const renderServiceImageGallery = (images: string[], title: string) => {
    const list = images.slice(0, 3);
    if (list.length === 0) return null;

    if (list.length === 1) {
      return (
        <div className="relative h-[220px] md:h-[240px] overflow-hidden">
          <img src={list[0]} alt={title} className="w-full h-full object-cover" />
        </div>
      );
    }

    if (list.length === 2) {
      return (
        <div className="grid grid-cols-2 gap-1.5 h-[220px] md:h-[240px] p-1.5 bg-muted/20">
          {list.map((image, index) => (
            <div key={`${image}-${index}`} className="overflow-hidden rounded-md">
              <img src={image} alt={`${title} ${index + 1}`} className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 gap-1.5 h-[220px] md:h-[240px] p-1.5 bg-muted/20">
        <div className="row-span-2 overflow-hidden rounded-md">
          <img src={list[0]} alt={`${title} 1`} className="w-full h-full object-cover" />
        </div>
        <div className="overflow-hidden rounded-md">
          <img src={list[1]} alt={`${title} 2`} className="w-full h-full object-cover" />
        </div>
        <div className="overflow-hidden rounded-md">
          <img src={list[2]} alt={`${title} 3`} className="w-full h-full object-cover" />
        </div>
      </div>
    );
  };

  const getServicePriceInfo = useCallback((service: GroupedService) => {
    if (service.type === "tour") {
      const isFOC = !!service.data?.free_of_charge;
      const overridePrice = getTourPriceOverride(service.data);
      const rawPrice = isFOC ? 0 : (overridePrice ?? getPrice(service.data?.tours, proposalPartnerBusinessType, false));
      const price = getAdjustedProposalPrice(rawPrice);
      const pricingType = service.data?.tours?.pricing_type;
      return { price, pricingType, isFOC };
    }
    if (service.type === "attraction") {
      const isFOC = !!service.data?.free_of_charge;
      const price = getAdjustedProposalPrice(getPrice(service.data?.attraction_tickets, proposalPartnerBusinessType, isFOC));
      const pricingType = service.data?.attraction_tickets?.pricing_type;
      return { price, pricingType, isFOC };
    }
    if (service.type === "transfer") {
      const isFOC = !!service.data?.free_of_charge;
      const price = getAdjustedProposalPrice(getPrice(service.data?.transfers, proposalPartnerBusinessType, isFOC));
      const pricingType = service.data?.transfers?.pricing_type;
      return { price, pricingType, isFOC };
    }
    if (service.type === "additional_request") {
      const isFOC = service.data?.is_selected_by_client === false ? false : false; // additional requests don't have FOC
      const quantity = Math.max(1, Number(service.data?.request_quantity || 1));
      const price = getAdjustedProposalPrice((service.data?.request_price || 0) * quantity);
      return { price, pricingType: null, isFOC };
    }
    return { price: 0, pricingType: null, isFOC: false };
  }, [getAdjustedProposalPrice, proposalPartnerBusinessType]);

  const computeDayTotal = (day: DailyService) => {
    try {
      return day.services.reduce((sum, s) => {
        const info = getServicePriceInfo(s);
        const serviceId = s.data?.id;
        const serviceType = s.type;
        const isSelected = isServiceSelected(serviceType, serviceId);
        
        if (!isSelected) return sum;
        
        if (s.type === "additional_request") {
          return sum + (s.data?.is_selected_by_client === false ? 0 : Number(info.price || 0));
        }
        if (info.isFOC) return sum;
        return sum + Number(info.price || 0);
      }, 0);
    } catch {
      return 0;
    }
  };


  const getCategoryKey = (service: GroupedService): string => {
    switch (service.type) {
      case "transfer":
        return "transfers";
      case "tour":
        return "tours";
      case "attraction":
        return "attractions";
      case "additional_request":
        return "additionalRequests";
      default:
        return "others";
    }
  };

  const getCategoryLabel = (key: string) => {
    switch (key) {
      case "transfers":
        return t.transfers;
      case "tours":
        return t.tours;
      case "attractions":
        return t.attractions;
      case "additionalRequests":
        return t.additionalRequests;
      default:
        return t.services;
    }
  };

  const formatTime = (time?: string | null) => {
    if (!time) return "--:--";
    const raw = String(time).trim();
    const m = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m) {
      const hh = m[1].padStart(2, "0");
      const mm = m[2];
      return `${hh}:${mm}`;
    }
    const d = new Date(raw);
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    if (raw.length >= 5 && raw[2] === ":") return raw.slice(0, 5);
    return raw;
  };

  const formatMealOption = (option?: string | null) => {
    if (!option) return "";
    const normalized = option.trim().toUpperCase();

    const translations =
      effectiveLanguage === "pt_br"
        ? {
            RO: "Somente hospedagem",
            BB: "Café da manhã incluso",
            HB: "Meia pensão",
            FB: "Pensão completa",
            AI: "All inclusive",
            UAI: "Ultra all inclusive",
            MAP: "Pensão americana modificada",
            FAP: "Pensão americana completa",
            EP: "Plano europeu",
          }
        : {
            RO: "Room only",
            BB: "Breakfast included",
            HB: "Half board",
            FB: "Full board",
            AI: "All inclusive",
            UAI: "Ultra all inclusive",
            MAP: "Modified American Plan",
            FAP: "Full American Plan",
            EP: "European Plan",
          };

    return translations[normalized as keyof typeof translations] ?? option;
  };

  const parseNightRate = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const getAccommodationComparisonRates = (accommodation: any) => {
    const hcteNightRate = parseNightRate(accommodation?.hcte_night_rate ?? accommodation?.booking_price);
    const accommodationWebsiteNightRate = parseNightRate(accommodation?.accommodation_website_night_rate);
    const bookingComNightRate = parseNightRate(accommodation?.booking_com_night_rate);

    return [
      { key: "hcte", label: t.hcteNightRate, value: hcteNightRate },
      { key: "website", label: t.accommodationWebsiteNightRate, value: accommodationWebsiteNightRate },
      { key: "booking", label: t.bookingComNightRate, value: bookingComNightRate },
    ].filter((rate) => rate.value !== null);
  };

  const renderMap = (mapQuery?: string | null) => {
    if (!mapQuery) return null;
    const mapUrl = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;
    return (
      <Card className="h-full overflow-hidden border-primary/10 shadow-xl rounded-2xl">
        <CardHeader className="flex flex-row items-center gap-3 pb-4">
          <div className="p-2.5 rounded-xl bg-primary/10">
            <MapIcon className="h-5 w-5 text-primary" />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-lg font-semibold leading-tight">{t.locationMap}</CardTitle>
            <CardDescription className="text-sm leading-relaxed">{t.stayOverview}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="pt-0 flex flex-col h-full gap-3">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
            <MapPin className="h-4 w-4" />
            <span>{t.locationMap}</span>
          </div>
          <div className="relative overflow-hidden rounded-2xl border bg-muted/40 shadow-inner flex-1 min-h-[320px] lg:min-h-[380px]">
            <iframe
              title={`map-${mapQuery}`}
              src={mapUrl}
              loading="lazy"
              className="h-full w-full border-0"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-primary/10" />
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderPublicHeader = () => (
    <header className="sticky top-0 z-50 bg-gradient-to-r from-primary/90 to-primary/80 backdrop-blur-md border-b border-primary/15 shadow-sm">
      <div className="container mx-auto px-4 py-2 md:py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <img src={hcteLogo} alt="HCTE Logo" className="h-10 w-10 md:h-12 md:w-12 rounded-xl" />
            <div className="leading-tight">
              <h1 className="text-xl md:text-2xl font-semibold text-primary-foreground tracking-tight">HCTE</h1>
              <p className="text-[11px] md:text-sm text-primary-foreground/70">HighClass Travel Experiences</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6 text-primary-foreground/90">
            <a
              href="https://www.highclassexperiences.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-primary-foreground transition-colors"
              aria-label="Website www.highclassexperiences.com"
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm">www.highclassexperiences.com</span>
            </a>
          </div>
          <div className="flex md:hidden items-center gap-2 text-primary-foreground/90">
            <a
              href="https://www.highclassexperiences.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-full hover:bg-primary/20 transition-colors"
              aria-label="Abrir website"
              title="www.highclassexperiences.com"
            >
              <Globe className="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>
    </header>
  );

  const renderContactCard = (description: string) => (
    <Card className="border-primary/20 shadow-xl bg-gradient-to-br from-card via-card to-muted/20">
      <CardHeader>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 rounded-xl bg-primary/15">
            <MessageSquare className="h-6 w-6 text-primary" />
          </div>
          <div>
            <CardTitle className="text-2xl">{t.contactInformation}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 p-4 rounded-xl border border-border/60 bg-background/60 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg bg-primary/10">
              <Headphones className="h-5 w-5 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">{t.supportTeamTitle}</p>
              <p className="text-sm text-muted-foreground">{t.supportTeamDescription}</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-muted/30 p-4 space-y-3 shadow-sm">
          <div className="text-sm font-semibold text-foreground">HighClass Travel Experiences</div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              <span>+971 54 420 0402</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span>contato@highclassexperiences.com</span>
            </div>
            <a
              href="https://www.highclassexperiences.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:text-primary transition-colors"
              aria-label="Website www.highclassexperiences.com"
            >
              <Globe className="h-4 w-4" />
              <span className="text-sm">www.highclassexperiences.com</span>
            </a>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const renderCanceledState = () => (
    <TooltipProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        {renderPublicHeader()}
        <main className="container mx-auto px-4 py-10 md:py-14">
          <div className="mx-auto max-w-4xl space-y-8">
            <section className="relative overflow-hidden rounded-[2rem] border border-rose-200/80 bg-[linear-gradient(135deg,rgba(255,255,255,0.98),rgba(255,247,237,0.96))] p-8 shadow-xl shadow-rose-100/60 md:p-10">
              <div className="absolute right-0 top-0 h-40 w-40 rounded-full bg-rose-200/30 blur-3xl" />
              <div className="relative space-y-6">
                <span className="inline-flex items-center gap-2 rounded-full border border-rose-300/70 bg-rose-50 px-4 py-1.5 text-sm font-semibold text-rose-700">
                  <Shield className="h-4 w-4" />
                  {t.proposalCanceledBadge}
                </span>

                <div className="max-w-2xl space-y-3">
                  <h1 className="text-3xl font-extrabold tracking-tight text-foreground md:text-5xl">
                    {t.proposalCanceledTitle}
                  </h1>
                </div>

                <div className="inline-flex max-w-2xl items-start gap-3 rounded-2xl border border-border/60 bg-background/85 px-4 py-4 shadow-sm">
                  <div className="rounded-xl bg-primary/10 p-2.5">
                    <Headphones className="h-5 w-5 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold text-foreground">{t.proposalCanceledContactPrompt}</p>
                    <p className="text-sm text-muted-foreground">{t.supportTeamDescription}</p>
                  </div>
                </div>
              </div>
            </section>

            {renderContactCard(t.proposalCanceledContactPrompt)}
          </div>
        </main>
      </div>
    </TooltipProvider>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-background via-muted/30 to-background">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (publicPageState === "canceled") {
    return renderCanceledState();
  }

  if (!proposal) {
    return (
      <TooltipProvider>
        <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
          {renderPublicHeader()}
          <main className="container mx-auto flex min-h-[calc(100vh-73px)] items-center justify-center px-4 py-12">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Proposal Not Found</CardTitle>
                <CardDescription>The requested proposal could not be found.</CardDescription>
              </CardHeader>
            </Card>
          </main>
        </div>
      </TooltipProvider>
    );
  }

  const client = proposal.clients;

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        {renderPublicHeader()}
      {isProposalApproved && (
        <div className="sticky top-[58px] md:top-[66px] z-40">
          <div className="container mx-auto px-4 py-3">
            <div className="mx-auto max-w-5xl rounded-xl border border-emerald-300/70 bg-emerald-50/95 px-4 py-3 shadow-sm backdrop-blur-sm">
              <div className="flex items-center justify-center gap-3 text-center text-emerald-900">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-200/80">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <p className="text-sm md:text-base font-semibold leading-snug text-center">{approvedBannerMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="relative overflow-hidden -mt-1">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-accent/10" />
        <div className="container mx-auto px-4 py-10 md:py-12 relative">
          <div className="max-w-5xl mx-auto text-center space-y-3 md:space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 shadow-sm">
              <FileCheck className="h-4 w-4 text-primary" />
              <span className="text-xs md:text-sm font-medium text-primary">
                {t.travelProposal} #{proposal?.internal_reference || 'N/A'}
              </span>
              {proposal?.sent_at && expiryDateIso && (
                <>
                  <span className="text-primary/50">–</span>
                  {isExpiredNow ? (
                    <span className="text-xs md:text-sm font-medium text-primary">{t.expired}</span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <span className="text-xs md:text-sm font-medium text-primary">{t.expiresInPrefix}</span>
                      <ProposalCountdown
                        expiryDate={expiryDateIso}
                        locale={locale}
                        variant="compact"
                        expiredLabel={t.expired}
                      />
                    </span>
                  )}
                </>
              )}
            </div>
            <p className="text-3xl md:text-4xl font-extrabold tracking-tight leading-tight text-foreground">
              {proposal?.title}
            </p>
            <p className="text-base md:text-lg text-muted-foreground/90 max-w-3xl mx-auto leading-snug font-medium">
              {t.readyToConfirm}
            </p>

            <div className="rounded-2xl border border-primary/15 bg-background/60 backdrop-blur-sm shadow-xl shadow-primary/5 p-2 md:p-3">
              <div className="grid grid-cols-1 md:grid-cols-10 gap-2 md:gap-3">
                <Card className="md:col-span-4 border border-border/60 bg-background/80 shadow-sm">
                  <CardContent className="flex flex-col items-center justify-center min-h-[120px] py-4 space-y-2">
                    <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary/15">
                      <MapPin className="h-5 w-5 text-primary" />
                    </span>
                    <p className="text-[15px] font-semibold text-muted-foreground">{t.destination}</p>
                    <p className="text-base font-bold text-foreground">{proposal.main_destination}</p>
                  </CardContent>
                </Card>

                <Card className="md:col-span-3 border border-border/60 bg-background/80 shadow-sm">
                  <CardContent className="flex flex-col items-center justify-center min-h-[120px] py-4 space-y-2">
                    <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary/15">
                      <Users className="h-5 w-5 text-primary" />
                    </span>
                    <p className="text-[15px] font-semibold text-muted-foreground">{t.passengers}</p>
                    <p className="text-base font-bold text-foreground">
                      {proposal.pax_adults} {t.adults}, {proposal.pax_children} {t.children}
                    </p>
                  </CardContent>
                </Card>

                <Card className="md:col-span-3 border border-border/60 bg-background/80 shadow-sm">
                  <CardContent className="flex flex-col items-center justify-center min-h-[120px] py-4 space-y-2">
                    <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-primary/15">
                      <Calendar className="h-5 w-5 text-primary" />
                    </span>
                    <p className="text-[15px] font-semibold text-muted-foreground">{t.travelDates}</p>
                    <p className="text-base font-bold text-foreground">
                      {travelDateRange.start ? formatDate(travelDateRange.start) : "--"}
                      <ArrowRight className="inline h-4 w-4 mx-1 text-muted-foreground" />
                      {travelDateRange.end ? formatDate(travelDateRange.end) : "--"}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

          </div>
        </div>
      </section>


      <section className="container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto space-y-12">
          {accommodations.length > 0 && (
            <Collapsible open={accommodationsOpen} onOpenChange={setAccommodationsOpen} className="space-y-4">
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/10 bg-muted/40 px-4 py-3 cursor-pointer transition hover:border-primary/30">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
                      <Hotel className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold tracking-tight">{t.accommodation}</h3>
                      <p className="text-muted-foreground text-base">{t.stayOverview}</p>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                    <span>{accommodationsOpen ? t.hide : t.show}</span>
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${accommodationsOpen ? "rotate-180" : "rotate-0"}`}
                    />
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-10 pt-2">
                {accommodations.map((acc, index) => {
                  const mapQuery = acc.hotels?.hotel_name || acc.hotels?.address || acc.hotels?.location;
                  const address = acc.hotels?.address || acc.hotels?.location;
                  const hasMap = Boolean(mapQuery);
                  const reviewCount = acc.hotels?.review_count;
                  const selected = isServiceSelected("accommodation", acc.id);
                  const comparisonRates = acc.booking_with_us ? getAccommodationComparisonRates(acc) : [];
                  const cancellationPolicy = String(acc.cancellation_policy || "").trim();
                  const cancellationPolicyKey = String(acc.id ?? index);
                  const isCancellationPolicyOpen = !!cancellationPolicyOpen[cancellationPolicyKey];

                  return (
                    <div
                      key={`accommodation_${acc.id}`}
                      className={`relative grid grid-cols-1 gap-6 ${hasMap ? "xl:grid-cols-[1.15fr_1fr]" : ""} xl:items-stretch`}
                    >
                      {hasMap && (
                        <div className="hidden xl:block absolute left-[55%] top-4 bottom-4 w-px bg-gradient-to-b from-transparent via-muted to-transparent" />
                      )}
                      <Card 
                        className={`overflow-hidden shadow-xl hover:shadow-2xl transition-colors duration-300 hover:-translate-y-1 rounded-2xl cursor-pointer ${
                          selected 
                            ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800" 
                            : "border-primary/10"
                        }`}
                        onClick={() => {
                          if (!canEditProposalSelections) return;
                          toggleServiceSelection("accommodation", acc.id);
                        }}
                      >
                        {acc.hotels?.image_url && (
                          <div className="relative h-64 overflow-hidden">
                            <img
                              src={acc.hotels.image_url}
                              alt={acc.hotels.hotel_name}
                              className="w-full h-full object-cover hover:scale-110 transition-transform duration-700"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-black/10 to-transparent" />
                            <div className={`absolute top-4 left-4 h-6 w-6 rounded border-2 flex items-center justify-center ${
                              selected 
                                ? "bg-emerald-500 border-emerald-500" 
                                : "bg-white/80 border-muted-foreground/40"
                            }`}>
                              {selected && <CheckCircle2 className="h-5 w-5 text-white" />}
                            </div>
                          </div>
                        )}
                        <CardHeader className="space-y-3">
                          <div className="flex flex-wrap items-center gap-3 justify-between">
                            <div className="flex items-center gap-3">
                              {!acc.hotels?.image_url && (
                                <div className={`h-6 w-6 rounded border-2 flex items-center justify-center ${
                                  selected 
                                    ? "bg-emerald-500 border-emerald-500" 
                                    : "border-muted-foreground/40"
                                }`}>
                                  {selected && <CheckCircle2 className="h-5 w-5 text-white" />}
                                </div>
                              )}
                              <div className="space-y-1">
                                <CardTitle className="text-2xl font-semibold leading-tight flex items-center gap-3">
                                  <span>{acc.hotels?.hotel_name}</span>
                                  {acc.hotels?.stars && (
                                    <span className="flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 text-sm font-semibold text-amber-600 border border-amber-100">
                                      <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
                                      {acc.hotels.stars}.0
                                    </span>
                                  )}
                                </CardTitle>
                                {reviewCount && (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                    <span className="font-medium">{reviewCount.toLocaleString()} {t.reviews}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {acc.meal_option && (
                              <span className="px-3 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary border border-primary/20">
                                {formatMealOption(acc.meal_option)}
                              </span>
                            )}
                          </div>

                          <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                            {address && (
                              <span className="inline-flex items-center gap-2 rounded-full bg-muted px-3 py-1.5 text-foreground">
                                <span role="img" aria-label="address">📍</span>
                                <span className="font-medium">{address}</span>
                              </span>
                            )}
                            <span className="inline-flex items-center gap-2 rounded-full bg-primary/5 px-3 py-1.5 text-primary font-medium">
                              <span role="img" aria-label="dates">🗓️</span>
                              <span>
                                {formatDate(acc.check_in)} {t.to} {formatDate(acc.check_out)}
                              </span>
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-5 pt-0">
                          {acc.hotel_rooms && (
                            <div className="rounded-2xl border bg-muted/40 p-4 space-y-3">
                              <div className="flex items-center gap-2">
                                <BedDouble className="h-5 w-5 text-primary" />
                                <p className="font-semibold text-base">{acc.hotel_rooms.room_name}</p>
                              </div>
                              {acc.hotel_rooms.room_description && (
                                <ExpandableDescription text={acc.hotel_rooms.room_description} />
                              )}
                            </div>
                          )}
                          {acc.hotels?.description && <ExpandableDescription text={acc.hotels.description} />}
                          {Array.isArray(acc.hotels?.amenities) && acc.hotels.amenities.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {acc.hotels.amenities.slice(0, 6).map((amenity: string, amenityIndex: number) => (
                                <span
                                  key={amenityIndex}
                                  className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-muted-foreground"
                                >
                                  {amenity}
                                </span>
                              ))}
                            </div>
                          )}
                          {acc.booking_with_us && comparisonRates.length > 0 && (
                            <div className="rounded-xl border border-border/70 bg-background/40 p-3.5 space-y-2.5">
                              <div className="flex items-center gap-2">
                                <Circle className="h-3.5 w-3.5 fill-primary/20 text-primary" />
                                <p className="text-sm font-semibold text-primary">{t.bookingWithUs}</p>
                              </div>
                              <p className="text-xs text-muted-foreground">{t.nightRateComparison}</p>
                              <div
                                className={`grid grid-cols-1 gap-2 ${
                                  comparisonRates.length === 1
                                    ? "md:grid-cols-1"
                                    : comparisonRates.length === 2
                                    ? "md:grid-cols-2"
                                    : "md:grid-cols-3"
                                }`}
                              >
                                {comparisonRates.map((rate) => {
                                  return (
                                    <div
                                      key={rate.key}
                                      className="rounded-lg border border-border/70 bg-background px-3 py-2.5 min-w-0 h-full flex flex-col justify-between"
                                    >
                                      <p className="text-xs md:text-sm font-medium text-muted-foreground leading-5 min-h-[2.25rem]">
                                        {rate.label}
                                      </p>
                                      <div className="mt-2 border-t border-border/60 pt-2">
                                        <p className="text-sm md:text-base font-semibold text-foreground tabular-nums whitespace-nowrap">
                                          {formatProposalMoney(rate.value ?? 0)}
                                        </p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {cancellationPolicy && (
                            <div className="rounded-xl border border-border/70 bg-background/40 p-3.5 space-y-2">
                              <button
                                type="button"
                                className="flex w-full items-center justify-between gap-3 text-left"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setCancellationPolicyOpen((prev) => ({
                                    ...prev,
                                    [cancellationPolicyKey]: !prev[cancellationPolicyKey],
                                  }));
                                }}
                              >
                                <p className="text-sm font-semibold text-foreground">{t.cancellationPolicy}</p>
                                <ChevronDown
                                  className={`h-4 w-4 text-muted-foreground transition-transform ${
                                    isCancellationPolicyOpen ? "rotate-180" : "rotate-0"
                                  }`}
                                />
                              </button>
                              {isCancellationPolicyOpen && (
                                <ExpandableDescription text={cancellationPolicy} />
                              )}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {hasMap && renderMap(mapQuery)}
                    </div>
                  );
                })}
              </CollapsibleContent>
            </Collapsible>
          )}

          {(flights.length > 0 || airportServices.length > 0) && (
            <Collapsible open={flightsOpen} onOpenChange={setFlightsOpen} className="space-y-4">
              <CollapsibleTrigger asChild>
                <div className="flex items-center justify-between gap-4 rounded-xl border border-primary/10 bg-muted/40 px-4 py-3 cursor-pointer transition hover:border-primary/30">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
                      <Plane className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold">{t.flights}</h3>
                      <p className="text-muted-foreground">{t.flightOverview}</p>
                    </div>
                  </div>
                  <div className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                    <span>{flightsOpen ? t.hide : t.show}</span>
                    <ChevronDown
                      className={`h-5 w-5 transition-transform ${flightsOpen ? "rotate-180" : "rotate-0"}`}
                    />
                  </div>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="space-y-6 pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {flights.map((flight, index) => {
                  const relatedServices = getAirportServicesForDate(flight.departure_datetime);
                  const flightId = flight?.id;
                  const flightBookingPrice = parseNightRate(flight?.booking_price);
                  const canSelectFlight = !!flight.booking_with_us && flightId !== null && flightId !== undefined && flightBookingPrice !== null;
                  const selectedFlight = canSelectFlight ? isServiceSelected("flight", flightId) : false;
                  const flightCode = (flight?.flight_number ?? "").toString().match(/^[A-Za-z]+/)?.[0]?.slice(0, 2).toUpperCase() || "";

                  return (
                    <Card
                      key={flightId ?? index}
                      className={`rounded-2xl shadow-md hover:shadow-lg transition-all ${
                        canSelectFlight
                          ? selectedFlight
                            ? "border-emerald-200 bg-emerald-50/70 dark:border-emerald-800 dark:bg-emerald-950/20"
                            : "border-primary/10"
                          : "border-primary/10"
                      }`}
                    >
                      <CardContent className="p-6 md:p-7 space-y-5">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="h-11 w-11 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center">
                              {flightCode ? (
                                <span className="text-sm font-bold text-primary">{flightCode}</span>
                              ) : (
                                <Plane className="h-5 w-5 text-primary" />
                              )}
                            </div>
                            <div className="leading-tight min-w-0">
                              <p className="font-bold text-lg truncate">{flight.airline}</p>
                              <p className="text-xs md:text-sm text-muted-foreground truncate">
                                {t.flightNumber} {(flight?.flight_number ?? "").toString().toUpperCase()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2.5 shrink-0">
                            {flight?.flight_class && (
                              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1.5 whitespace-nowrap">
                                <span className="text-[10px] font-bold uppercase tracking-wide text-primary/60">{t.class}</span>
                                <span className="text-xs font-semibold text-primary">{flight.flight_class}</span>
                              </span>
                            )}
                            {canSelectFlight ? (
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                if (!canEditProposalSelections) return;
                                toggleServiceSelection("flight", flightId);
                              }}
                              className={`h-6 w-6 rounded border-2 flex items-center justify-center shrink-0 ${
                                selectedFlight
                                  ? "bg-emerald-500 border-emerald-500"
                                  : "bg-white/80 border-muted-foreground/40"
                              }`}
                              aria-label="Toggle flight selection"
                            >
                              {selectedFlight && <CheckCircle2 className="h-5 w-5 text-white" />}
                            </button>
                          ) : (
                            <span className="hidden md:inline-flex items-center gap-2 text-primary/70 font-medium">
                              <span role="img" aria-label="route">✈️</span>
                            </span>
                          )}
                          </div>
                        </div>

                        <div className="flex items-center justify-center gap-2 md:gap-3">
                          <span className="text-2xl md:text-3xl font-extrabold tracking-tight text-primary/90 uppercase">{flight.departure_airport}</span>
                          <span className="text-xl md:text-2xl text-foreground/60">→</span>
                          <span className="text-2xl md:text-3xl font-extrabold tracking-tight text-primary uppercase">{flight.arrival_airport}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-3 md:gap-4">
                          <div className="p-4 md:p-5 rounded-full md:rounded-xl bg-primary/5 text-center">
                            <p className="font-bold text-lg md:text-xl uppercase mb-0.5 flex items-center justify-center gap-1">
                              <span role="img" aria-label="departure">🛫</span> {flight.departure_airport}
                            </p>
                            <div className="text-xs md:text-sm text-muted-foreground flex items-center justify-center gap-2">
                              <span className="inline-flex items-center gap-1"><span role="img" aria-label="date">🗓️</span>{formatDate(flight.departure_datetime)}</span>
                              <span className="hidden md:inline">•</span>
                              <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDateTimeClock(flight.departure_datetime)}</span>
                            </div>
                          </div>
                          <div className="p-4 md:p-5 rounded-full md:rounded-xl bg-primary/10 text-center">
                            <p className="font-bold text-lg md:text-xl uppercase mb-0.5 flex items-center justify-center gap-1">
                              <span role="img" aria-label="arrival">🛬</span> {flight.arrival_airport}
                            </p>
                            <div className="text-xs md:text-sm text-muted-foreground flex items-center justify-center gap-2">
                              <span className="inline-flex items-center gap-1"><span role="img" aria-label="date">🗓️</span>{formatDate(flight.arrival_datetime)}</span>
                              <span className="hidden md:inline">•</span>
                              <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{formatDateTimeClock(flight.arrival_datetime)}</span>
                            </div>
                          </div>
                        </div>

                        {!!flight.booking_with_us && flightBookingPrice !== null && (
                          <div className="rounded-xl border border-border/70 bg-background/40 p-3.5">
                            <p className="text-xs text-muted-foreground">{t.flightBookingPrice}</p>
                            <p className="text-base font-semibold text-primary">
                              {formatProposalMoney(flightBookingPrice)}
                            </p>
                          </div>
                        )}

                        <div className="pt-3 mt-1 border-t border-border/60">
                          {relatedServices.length > 0 ? (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                                <Luggage className="h-4 w-4" />
                                {t.airportServices}
                              </div>
                              {relatedServices.map((service, serviceIndex) => {
                                const serviceId = service.id;
                                const selected = isServiceSelected("airport_service", serviceId);
                                return (
                                  <div
                                    key={`airport_service_${serviceId ?? serviceIndex}`}
                                    className={`rounded-xl border p-3.5 md:p-4 cursor-pointer transition-colors ${
                                      selected
                                        ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800"
                                        : "bg-background"
                                    }`}
                                    onClick={() => {
                                      if (!canEditProposalSelections) return;
                                      if (serviceId !== null && serviceId !== undefined) {
                                        toggleServiceSelection("airport_service", serviceId);
                                      }
                                    }}
                                  >
                                    <div className="flex items-start justify-between gap-3">
                                      <div className="min-w-0 flex items-start gap-3">
                                        <div className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center ${
                                          selected
                                            ? "bg-emerald-500 border-emerald-500"
                                            : "border-muted-foreground/40"
                                        }`}>
                                          {selected && <CheckCircle2 className="h-4 w-4 text-white" />}
                                        </div>
                                        <div>
                                          <p className="font-medium text-sm md:text-base">
                                            {service.airport_services?.service_name}
                                          </p>
                                          {service.airport_services?.description && (
                                            <div className="mt-1.5">
                                              <ExpandableDescription
                                                text={service.airport_services.description}
                                                className="text-xs"
                                              />
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right">
                                        {service.free_of_charge ? (
                                          <p className="text-sm font-semibold text-primary">{t.freeOfCharge}</p>
                                        ) : (
                                          (() => {
                                            const pricing = getAirportServicePricing(service);
                                            return (
                                              <>
                                                <p className="text-sm font-semibold text-primary">{formatProposalMoney(pricing.total)}</p>
                                                {service.airport_services?.pricing_type && (
                                                  <p className="text-xs text-muted-foreground">
                                                    {getPricingTypeLabel(service.airport_services.pricing_type)}
                                                    {pricing.perPerson && (
                                                      <> · {formatProposalMoney(pricing.unit)} × {pricing.quantity}</>
                                                    )}
                                                  </p>
                                                )}
                                              </>
                                            );
                                          })()
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div className="rounded-xl border bg-muted/30 p-4 text-muted-foreground text-sm text-center">
                              {t.noAirportServicesForFlight || "No airport services for this flight"}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                  })}
                </div>

                {standaloneAirportServices.length > 0 && (
                  <Card className="rounded-2xl border-primary/10 shadow-md">
                    <CardHeader className="border-t pt-3">
                      <CardTitle className="flex items-center gap-2">
                        <Luggage className="h-5 w-5 text-primary" />
                        {t.airportServices}
                      </CardTitle>
                      <CardDescription>{t.standaloneAirportServices}</CardDescription>
                    </CardHeader>
                    <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {standaloneAirportServices.map((service, index) => {
                        const serviceId = service.id;
                        const selected = isServiceSelected("airport_service", serviceId);
                        return (
                          <div
                            key={`standalone_airport_service_${serviceId ?? index}`}
                            className={`rounded-xl border p-4 cursor-pointer transition-colors ${
                              selected
                                ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800"
                                : "bg-muted/30"
                            }`}
                            onClick={() => {
                              if (!canEditProposalSelections) return;
                              if (serviceId !== null && serviceId !== undefined) {
                                toggleServiceSelection("airport_service", serviceId);
                              }
                            }}
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                              <div className="min-w-0 flex items-start gap-3">
                                <div className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center ${
                                  selected
                                    ? "bg-emerald-500 border-emerald-500"
                                    : "border-muted-foreground/40"
                                }`}>
                                  {selected && <CheckCircle2 className="h-4 w-4 text-white" />}
                                </div>
                                <div className="leading-tight">
                                  <p className="font-semibold">{service.airport_services?.service_name}</p>
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <span role="img" aria-label="date">🗓️</span>{formatDate(service.service_date)}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right text-sm font-semibold text-primary">
                                {service.free_of_charge ? (
                                  t.freeOfCharge
                                ) : (
                                  (() => {
                                    const pricing = getAirportServicePricing(service);
                                    return (
                                      <>
                                        {formatProposalMoney(pricing.total)}
                                        {service.airport_services?.pricing_type && (
                                          <p className="text-xs font-normal text-muted-foreground">
                                            {getPricingTypeLabel(service.airport_services.pricing_type)}
                                            {pricing.perPerson && (
                                              <> · {formatProposalMoney(pricing.unit)} × {pricing.quantity}</>
                                            )}
                                          </p>
                                        )}
                                      </>
                                    );
                                  })()
                                )}
                              </div>
                            </div>
                            {service.airport_services?.airport && (
                              <div className="mt-1 text-xs text-muted-foreground">
                                <span className="uppercase font-medium">{service.airport_services.airport}</span>
                              </div>
                            )}
                            {service.airport_services?.description && (
                              <div className="mt-2">
                                <ExpandableDescription
                                  text={service.airport_services.description}
                                  className="text-xs md:text-sm"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                )}
              </CollapsibleContent>
            </Collapsible>
          )}

          {dailyServices.length > 0 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
                  <Calendar className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="text-3xl font-bold">{t.dailyPlan}</h3>
                  <p className="text-muted-foreground">{t.dailyPlanDescription}</p>
                </div>
              </div>
              <div className="space-y-4">
                {dailyServices.map((day, dayIndex) => (
                  <Collapsible key={dayIndex} defaultOpen>
                    <Card className="border-primary/10 shadow-md overflow-hidden">
                      <CollapsibleTrigger className="w-full">
                        <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 rounded-lg bg-primary/10">
                                <Calendar className="h-5 w-5 text-primary" />
                              </div>
                              <div className="text-left">
                                <CardTitle className="text-xl flex items-center gap-2">
                                  <span>{formatDate(day.date)}</span>
                                  <span className="text-muted-foreground text-base">– {formatWeekday(day.date)}</span>
                                </CardTitle>
                                <div className="flex items-center gap-2 mt-1">
                                  <span
                                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                      day.services.length > 3 ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                                    }`}
                                  >
                                    {day.services.length} {t.servicesLabel}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {t.totalOfSelectedItemsThisDay}: {formatMoney(computeDayTotal(day), currency, locale)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <ChevronDown className="h-5 w-5 text-muted-foreground transition-transform" />
                          </div>
                        </CardHeader>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <CardContent className="space-y-6 pt-2">
                          <div className="flex flex-col md:flex-row gap-6">
                            <div className="md:w-1/4">
                              <div className="relative pl-4 md:pl-6">
                                <div className="absolute left-0 top-1 bottom-1 w-px bg-border md:left-1.5" />
                                <div className="space-y-3">
                                  {day.services
                                    .filter((s) => !!s.time && isServiceMarkedByClient(s))
                                    .map((s, i) => (
                                      <div
                                        key={i}
                                        className="relative grid grid-cols-[56px_1fr] items-center gap-2"
                                      >
                                        <div className="absolute -left-2.5 md:-left-3 h-2.5 w-2.5 rounded-full bg-primary" />
                                        <span className="text-xs font-medium text-muted-foreground tabular-nums">
                                          {formatTime(s.time || "")}
                                        </span>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span className="text-xs truncate block cursor-default min-w-0">
                                              {getServiceTitle(s)}
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent side="top" align="start" className="max-w-sm">
                                            {getServiceTitle(s)}
                                          </TooltipContent>
                                        </Tooltip>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                            <div className="md:w-3/4 space-y-6">
                              {(() => {
                                const transfersGroup = day.services.filter((s) => s.type === "transfer");
                                const unifiedGroup = day.services.filter((s) => s.type !== "transfer");
                                return (
                                  <>
                                    {transfersGroup.length > 0 && (
                                      <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-semibold text-muted-foreground">{t.transfers}</span>
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                            {transfersGroup.length}
                                          </span>
                                        </div>
                                        <div className="space-y-4">
                                          {transfersGroup.map((service, serviceIndex) => {
                                            const title = getServiceTitle(service);
                                            const description = getServiceDescription(service);
                                            const duration = getServiceDuration(service);
                                            const images = getServiceImages(service);
                                            const { price, pricingType, isFOC } = getServicePriceInfo(service);
                                            const serviceId = service.data?.id;
                                            const selected = isServiceSelected("transfer", serviceId);
                                            return (
                                              <Card 
                                                key={`transfer_${serviceId}`} 
                                                className={`overflow-hidden border-muted cursor-pointer transition-colors ${
                                                  selected 
                                                    ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800" 
                                                    : ""
                                                }`}
                                                onClick={() => {
                                                  if (!canEditProposalSelections) return;
                                                  toggleServiceSelection("transfer", serviceId);
                                                }}
                                              >
                                                {renderServiceImageGallery(images, title)}
                                                <CardContent className="p-5 space-y-2">
                                                  <div className="flex items-start justify-between gap-3">
                                                    <div className="min-w-0 flex items-start gap-3">
                                                      <div className={`mt-1 h-5 w-5 rounded border-2 flex items-center justify-center ${
                                                        selected 
                                                          ? "bg-emerald-500 border-emerald-500" 
                                                          : "border-muted-foreground/40"
                                                      }`}>
                                                        {selected && <CheckCircle2 className="h-4 w-4 text-white" />}
                                                      </div>
                                                      <div>
                                                        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1">
                                                          {service.time && (
                                                            <span className="flex items-center gap-1">
                                                              <Clock className="h-3 w-3" />
                                                              {formatTime(service.time)}
                                                            </span>
                                                          )}
                                                          {duration && (
                                                            <span className="flex items-center gap-1">
                                                              <Clock className="h-3 w-3" />
                                                              {t.duration}: {formatDurationHours(duration)} {t.hours}
                                                            </span>
                                                          )}
                                                        </div>
                                                        <h4 className="font-semibold text-base md:text-lg">{title}</h4>
                                                      </div>
                                                    </div>
                                                    <div className="text-right">
                                                      <div className="text-sm md:text-base font-semibold text-primary">
                                                        {isFOC ? t.freeOfCharge : formatMoney(price, currency, locale)}
                                                      </div>
                                                      {pricingType && !isFOC && (
                                                        <div className="text-[11px] text-muted-foreground">{getPricingTypeLabel(pricingType)}</div>
                                                      )}
                                                    </div>
                                                  </div>
                                                  <ServiceDescription text={description} />
                                                </CardContent>
                                              </Card>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    )}

                                    {unifiedGroup.length > 0 && (
                                      <div className="space-y-3">
                                        {(() => {
                                          const groupedUnifiedServices: Array<
                                            | { kind: "single"; service: GroupedService }
                                            | { kind: "attraction_group"; services: GroupedService[] }
                                          > = [];
                                          const attractionGroupIndex = new Map<string, number>();

                                          unifiedGroup.forEach((service) => {
                                            if (service.type !== "attraction") {
                                              groupedUnifiedServices.push({ kind: "single", service });
                                              return;
                                            }

                                            const key = [
                                              service.data?.attraction_id ?? service.data?.attractions?.id ?? "no_attraction",
                                              service.data?.attraction_date ?? "",
                                              service.data?.time_from ?? "",
                                              service.data?.time_to ?? "",
                                            ].join("__");

                                            const existingIndex = attractionGroupIndex.get(key);
                                            if (existingIndex === undefined) {
                                              attractionGroupIndex.set(key, groupedUnifiedServices.length);
                                              groupedUnifiedServices.push({ kind: "attraction_group", services: [service] });
                                            } else {
                                              const existing = groupedUnifiedServices[existingIndex];
                                              if (existing.kind === "attraction_group") {
                                                existing.services.push(service);
                                              }
                                            }
                                          });

                                          return (
                                            <>
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm font-semibold text-muted-foreground">{t.services}</span>
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                                                  {groupedUnifiedServices.length}
                                                </span>
                                              </div>
                                              <div className="space-y-4">
                                                {groupedUnifiedServices.map((groupedService, serviceIndex) => {
                                                  if (groupedService.kind === "single") {
                                                    const service = groupedService.service;
                                                    const title = getServiceTitle(service);
                                                    const description = getServiceDescription(service);
                                                    const duration = getServiceDuration(service);
                                                    const images = getServiceImages(service);
                                                    const { price, pricingType, isFOC } = getServicePriceInfo(service);
                                                    const serviceId = service.data?.id;
                                                    const serviceType = service.type;
                                                    const selected = isServiceSelected(serviceType, serviceId);

                                                    return (
                                                      <Card
                                                        key={`${serviceType}_${serviceId}_${serviceIndex}`}
                                                        className={`overflow-hidden border-muted cursor-pointer transition-colors ${
                                                          selected
                                                            ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800"
                                                            : ""
                                                        }`}
                                                        onClick={() => {
                                                          if (!canEditProposalSelections) return;
                                                          toggleServiceSelection(serviceType, serviceId);
                                                        }}
                                                      >
                                                        {renderServiceImageGallery(images, title)}
                                                        <CardContent className="p-5 space-y-2">
                                                          <div className="flex items-start justify-between gap-3">
                                                            <div className="min-w-0 flex items-start gap-3">
                                                              <div className={`mt-1 h-5 w-5 rounded border-2 flex items-center justify-center ${
                                                                selected
                                                                  ? "bg-emerald-500 border-emerald-500"
                                                                  : "border-muted-foreground/40"
                                                              }`}>
                                                                {selected && <CheckCircle2 className="h-4 w-4 text-white" />}
                                                              </div>
                                                              <div>
                                                                <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1">
                                                                  {service.time && (
                                                                    <span className="flex items-center gap-1">
                                                                      <Clock className="h-3 w-3" />
                                                                      {formatTime(service.time)}
                                                                    </span>
                                                                  )}
                                                                  {duration && (
                                                                    <span className="flex items-center gap-1">
                                                                      <Clock className="h-3 w-3" />
                                                                      {t.duration}: {formatDurationHours(duration)} {t.hours}
                                                                    </span>
                                                                  )}
                                                                </div>
                                                                <h4 className="font-semibold text-base md:text-lg">{title}</h4>
                                                              </div>
                                                            </div>
                                                            <div className="text-right">
                                                              <div className="text-sm md:text-base font-semibold text-primary">
                                                                {isFOC ? t.freeOfCharge : formatMoney(price, currency, locale)}
                                                              </div>
                                                              {pricingType && !isFOC && (
                                                                <div className="text-[11px] text-muted-foreground">{getPricingTypeLabel(pricingType)}</div>
                                                              )}
                                                            </div>
                                                          </div>
                                                          <ServiceDescription text={description} />
                                                        </CardContent>
                                                      </Card>
                                                    );
                                                  }

                                                  const services = groupedService.services;
                                                  const primaryService = services[0];
                                                  const title = primaryService?.data?.attractions?.title || "";
                                                  const description = getServiceDescription(primaryService);
                                                  const duration = getServiceDuration(primaryService);
                                                  const images = getServiceImages(primaryService);
                                                  const hasSelectedTicket = services.some((service) =>
                                                    isServiceSelected("attraction", service.data?.id)
                                                  );

                                                  return (
                                                    <Card
                                                      key={`attraction_group_${serviceIndex}`}
                                                      className={`overflow-hidden border-muted transition-colors ${
                                                        hasSelectedTicket
                                                          ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800"
                                                          : ""
                                                      }`}
                                                    >
                                                      {renderServiceImageGallery(images, title)}
                                                      <CardContent className="p-5 space-y-3">
                                                        <div className="flex items-start justify-between gap-3">
                                                          <div>
                                                            <div className="flex items-center gap-3 text-xs text-muted-foreground mb-1">
                                                              {primaryService?.time && (
                                                                <span className="flex items-center gap-1">
                                                                  <Clock className="h-3 w-3" />
                                                                  {formatTime(primaryService.time)}
                                                                </span>
                                                              )}
                                                              {duration && (
                                                                <span className="flex items-center gap-1">
                                                                  <Clock className="h-3 w-3" />
                                                                  {t.duration}: {formatDurationHours(duration)} {t.hours}
                                                                </span>
                                                              )}
                                                            </div>
                                                            <h4 className="font-semibold text-base md:text-lg">{title}</h4>
                                                          </div>
                                                        </div>

                                                        <ServiceDescription text={description} />

                                                        <div className="space-y-2 pt-1">
                                                          <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                                            {ticketTypeLabel}
                                                          </p>
                                                          {services.map((ticketService, ticketIndex) => {
                                                            const ticketServiceId = ticketService.data?.id;
                                                            const ticketSelected = isServiceSelected("attraction", ticketServiceId);
                                                            const ticketTitle =
                                                              ticketService.data?.attraction_tickets?.title || `${ticketLabel} ${ticketIndex + 1}`;
                                                            const ticketQuantity = Number(ticketService.data?.ticket_quantity ?? 1);
                                                            const ticketDescription = (ticketService.data?.attraction_tickets?.description || "")
                                                              .toString()
                                                              .trim();
                                                            const { price, pricingType, isFOC } = getServicePriceInfo(ticketService);

                                                            return (
                                                              <div
                                                                key={`attraction_ticket_${ticketServiceId ?? ticketIndex}`}
                                                                className={`rounded-lg border p-3 transition-colors ${
                                                                  ticketSelected
                                                                    ? "bg-emerald-100/70 border-emerald-300 dark:bg-emerald-900/30 dark:border-emerald-700"
                                                                    : "bg-background"
                                                                } ${ticketServiceId !== null && ticketServiceId !== undefined ? "cursor-pointer" : ""}`}
                                                                onClick={() => {
                                                                  if (!canEditProposalSelections) return;
                                                                  if (ticketServiceId === null || ticketServiceId === undefined) return;
                                                                  toggleServiceSelection("attraction", ticketServiceId);
                                                                }}
                                                              >
                                                                <div className="flex items-start justify-between gap-3">
                                                                  <div className="min-w-0 flex items-start gap-3">
                                                                    <div className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center ${
                                                                      ticketSelected
                                                                        ? "bg-emerald-500 border-emerald-500"
                                                                        : "border-muted-foreground/40"
                                                                    }`}>
                                                                      {ticketSelected && <CheckCircle2 className="h-4 w-4 text-white" />}
                                                                    </div>
                                                                    <div>
                                                                      <p className="font-medium text-sm md:text-base">{ticketTitle}</p>
                                                                      <div className="mt-1 inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                                                                        <span className="font-medium">{quantityLabel}:</span>
                                                                        <span className="ml-1 tabular-nums">{ticketQuantity}</span>
                                                                      </div>
                                                                      {ticketDescription && (
                                                                        <div className="mt-2 rounded-md bg-muted/40 px-2 py-1.5">
                                                                          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                                                                            {t.description}
                                                                          </p>
                                                                          <p className="text-xs text-foreground/80 leading-relaxed">
                                                                            {ticketDescription}
                                                                          </p>
                                                                        </div>
                                                                      )}
                                                                    </div>
                                                                  </div>
                                                                  <div className="text-right">
                                                                    <p className="text-sm font-semibold text-primary">
                                                                      {isFOC ? t.freeOfCharge : formatMoney(price, currency, locale)}
                                                                    </p>
                                                                    {pricingType && !isFOC && (
                                                                      <p className="text-xs text-muted-foreground">{getPricingTypeLabel(pricingType)}</p>
                                                                    )}
                                                                  </div>
                                                                </div>
                                                              </div>
                                                            );
                                                          })}
                                                        </div>
                                                      </CardContent>
                                                    </Card>
                                                  );
                                                })}
                                              </div>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </CardContent>
                      </CollapsibleContent>
                    </Card>
                  </Collapsible>
                ))}
              </div>
            </div>
          )}

          {insurance.length > 0 && (
            <Collapsible open={insuranceOpen} onOpenChange={setInsuranceOpen}>
              <Card className="border-primary/20 shadow-xl bg-gradient-to-br from-card via-card to-muted/20">
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
                          <Shield className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-2xl">{t.insurance}</CardTitle>
                          <CardDescription>{t.insuranceDescription}</CardDescription>
                        </div>
                      </div>
                      <div className="inline-flex items-center gap-2 text-sm font-medium text-primary">
                        <span>{insuranceOpen ? t.hide : t.show}</span>
                        <ChevronDown
                          className={`h-5 w-5 transition-transform ${insuranceOpen ? "rotate-180" : "rotate-0"}`}
                        />
                      </div>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="space-y-4">
                    {insurance.map((ins) => {
                      const selected = isServiceSelected("insurance", ins.id);
                      return (
                        <div
                          key={`insurance_${ins.id}`}
                          className={`rounded-xl border p-5 cursor-pointer transition-colors ${
                            selected
                              ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800"
                              : "bg-muted/30"
                          }`}
                          onClick={() => {
                            if (!canEditProposalSelections) return;
                            toggleServiceSelection("insurance", ins.id);
                          }}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`h-5 w-5 rounded border-2 flex items-center justify-center ${
                                selected
                                  ? "bg-emerald-500 border-emerald-500"
                                  : "border-muted-foreground/40"
                              }`}>
                                {selected && <CheckCircle2 className="h-4 w-4 text-white" />}
                              </div>
                              <Shield className="h-6 w-6 text-primary" />
                              <p className="font-semibold text-base md:text-lg">
                                {ins.insurance_title}
                              </p>
                            </div>
                            <div className="text-right text-sm md:text-base font-bold text-primary">
                              {formatProposalMoney(ins.insurance_price)}
                            </div>
                          </div>
                          {ins.insurance_description && (
                            <p className="mt-3 text-sm text-muted-foreground leading-relaxed pl-8">
                              {ins.insurance_description}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          )}

          {handlingFee && (
            <Card className="border-primary/20 shadow-xl bg-gradient-to-br from-card via-card to-muted/20">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
                    <Banknote className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl">{t.handlingFee}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-xl border p-5 bg-emerald-50 border-emerald-200 dark:bg-emerald-950/20 dark:border-emerald-800">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                      <Banknote className="h-6 w-6 text-primary" />
                      <div className="min-w-0">
                        <p className="font-semibold text-base md:text-lg">
                          {handlingFee.fee_title || t.handlingFee}
                        </p>
                        {handlingFee.fee_description && (
                          <p className="mt-1 text-sm text-muted-foreground leading-relaxed">
                            {handlingFee.fee_description}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right text-sm md:text-base font-bold text-primary whitespace-nowrap">
                      {formatProposalMoney(handlingFee.fee_price, { exempt: true })}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="border-primary/20 shadow-xl bg-gradient-to-br from-primary/5 via-card to-accent/5">
            <CardHeader className="pb-3 border-b border-border/60">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5">
                  <CreditCard className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-2xl">{t.priceSummary}</CardTitle>
                  <CardDescription>{t.summaryDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 pt-4">
              <div className="space-y-1">
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-md px-3 py-3 md:py-3.5 leading-relaxed odd:bg-muted/30 hover:bg-muted/40 transition-colors">
                  <span className="min-w-0 text-sm md:text-base text-foreground/80 pl-1.5">{t.subtotal}</span>
                  <span className="min-w-0 text-right font-semibold text-foreground text-sm md:text-base">
                    {formatMoney(financialSummary.totalBase, currency, locale)}
                  </span>
                </div>

                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-md px-3 py-3 md:py-3.5 leading-relaxed odd:bg-muted/30 hover:bg-muted/40 transition-colors">
                  <span className="min-w-0 text-sm md:text-base text-foreground/80 pl-1.5">{t.totalTaxes}</span>
                  <span className="min-w-0 text-right font-semibold text-foreground text-sm md:text-base">
                    {formatMoney(financialSummary.totalTaxes, currency, locale)}
                  </span>
                </div>
              </div>

              <div className="my-3 h-px bg-border/80" />

              <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 rounded-lg px-3 py-4 bg-[#F9FAFB]">
                <span className="min-w-0 text-base md:text-lg font-semibold pl-1.5">{t.total}</span>
                <div className="min-w-0 text-right">
                  <div className="text-[clamp(0.95rem,2.4vw,1.35rem)] font-semibold tracking-tight text-foreground whitespace-nowrap">
                    {formatMoney(proposalTotalPayable, currency, locale)}
                  </div>
                  <div className="mt-1 text-xs md:text-sm text-muted-foreground leading-snug break-words">
                    {convertedTotalLabel}: {convertedTotalValue}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {!isHistoryPreview && (
            <ProposalConfirmation
              status={proposal.status}
              isAccepting={isAccepting}
              onAccept={handleAcceptProposal}
              requireTermsAcceptance={requiresTermsAcceptance}
              t={{
                readyToConfirm: t.readyToConfirm,
                confirmationDescription: t.confirmationDescription,
                acceptProposal: t.acceptProposal,
                proposalConfirmed: t.proposalConfirmed,
                thankYouMessage: t.thankYouMessage,
                acceptTermsLabel: t.acceptTermsLabel,
                acceptTermsRequiredError: t.acceptTermsRequiredError,
              }}
            />
          )}

          {renderContactCard(t.contactDescription)}

          {(generalTerms || paymentTerms) && (
            <section className="mt-14 md:mt-16">
              <div className="max-w-4xl mx-auto">
                <div className="text-center mb-4 md:mb-6">
                  <p className="text-xs md:text-sm text-muted-foreground/80">
                    {t.additionalInfoTitle}
                  </p>
                </div>

                <div className="space-y-4">
                  {generalTerms && (
                    <Collapsible open={generalOpen} onOpenChange={setGeneralOpen} className="group">
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-4 rounded-xl border border-[#E4E8F1] bg-white/80 backdrop-blur-sm px-4 py-3 shadow-sm hover:shadow transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-lg bg-primary/10">
                              <Shield className="h-5 w-5 text-primary" />
                            </div>
                            <div className="text-left">
                              <h4 className="text-base font-semibold text-foreground">{t.generalTermsTitle}</h4>
                              <p className="hidden sm:block text-xs text-muted-foreground">{t.clickToView}</p>
                            </div>
                          </div>
                          <ChevronDown
                            className={`h-5 w-5 text-muted-foreground transition-transform duration-300 ${generalOpen ? "rotate-180" : "rotate-0"}`}
                            aria-hidden="true"
                          />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="overflow-hidden">
                        <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80 bg-white/70 border border-[#E4E8F1] border-t-0 rounded-b-xl px-4 py-4 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
                          {generalTerms}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}

                  {paymentTerms && (
                    <Collapsible open={paymentOpen} onOpenChange={setPaymentOpen} className="group">
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between gap-4 rounded-xl border border-[#E4E8F1] bg-white/80 backdrop-blur-sm px-4 py-3 shadow-sm hover:shadow transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-lg bg-primary/10">
                              <CreditCard className="h-5 w-5 text-primary" />
                            </div>
                            <div className="text-left">
                              <h4 className="text-base font-semibold text-foreground">{t.paymentTermsTitle}</h4>
                              <p className="hidden sm:block text-xs text-muted-foreground">{t.paymentInfoHint}</p>
                            </div>
                          </div>
                          <ChevronDown
                            className={`h-5 w-5 text-muted-foreground transition-transform duration-300 ${paymentOpen ? "rotate-180" : "rotate-0"}`}
                            aria-hidden="true"
                          />
                        </button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="overflow-hidden">
                        <div className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80 bg-white/70 border border-[#E4E8F1] border-t-0 rounded-b-xl px-4 py-4 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
                          {paymentTerms}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </div>
              </div>
            </section>
          )}

          {(!generalTerms && !paymentTerms && proposal?.terms_and_conditions) && (
            <div className="mt-12 pt-8 border-t border-border">
              <div className="max-w-4xl mx-auto">
                <button
                  onClick={() => setShowTerms(!showTerms)}
                  className="w-full flex items-center justify-between p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <Shield className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm font-medium text-foreground">{t.termsAndConditions}</span>
                  </div>
                  <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform duration-300 ${showTerms ? "rotate-180" : ""}`}
                  />
                </button>
                {showTerms && (
                  <div className="text-xs text-muted-foreground/80 leading-relaxed whitespace-pre-wrap mt-4 p-4 bg-muted/20 rounded-lg animate-in fade-in slide-in-from-top-2 duration-300">
                    {proposal.terms_and_conditions}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      <footer className="mt-20 bg-gradient-to-r from-primary/95 to-primary/80 text-primary-foreground">
        <div className="container mx-auto px-4 py-12">
          <div className="text-center space-y-4">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src={hcteLogo} alt="HCTE Logo" className="h-12 w-12" />
              <div className="text-left">
                <p className="text-xl font-bold">HCTE</p>
                <p className="text-sm text-primary-foreground/90">HighClass Travel Experiences</p>
              </div>
            </div>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto">
              Creating unforgettable journeys with personalized service and attention to detail
            </p>
            <div className="pt-6 border-t border-primary-foreground/20">
              <p className="text-sm text-primary-foreground/70">
                © 2024 HCTE HighClass Travel Experiences. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </TooltipProvider>
  );
};

export default PublicProposalView;
